// TUT-450 — os prazos e os tipos de prazo do escritório.
import { demoApi } from "../demo-data";
import type {
  AssignableMembersResponse,
  BusinessDaysPreview,
  CreateDeadlineInput,
  CreateOfficeHolidayInput,
  OfficeHoliday,
  OfficeHolidayListResponse,
  DeadlineTypeListResponse,
  DeadlineTypeSummary,
  DeadlineItem,
  DeadlineListResponse,
  UpdateDeadlineInput,
} from "../types";
import { comFatia, request } from "./infra";
import type { DeadlineListQuery } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDosPrazos = {
  deadlines({
    search = "",
    status = "",
    page,
    pageSize,
    sort,
    dir,
    type,
    assignedUserId,
    subjectId,
    clientOrganizationId,
    vence,
    dias,
    periodoDe,
    periodoAte,
    concluidoDe,
    concluidoAte,
    noPrazo,
  }: DeadlineListQuery = {}): Promise<DeadlineListResponse> {
    if (useDemoApi)
      return demoApi.deadlines({
        search,
        status,
        page,
        pageSize,
        sort,
        dir,
        ...(type ? { type } : {}),
        ...(assignedUserId ? { assignedUserId } : {}),
        ...(subjectId ? { subjectId } : {}),
        ...(clientOrganizationId ? { clientOrganizationId } : {}),
        ...(vence ? { vence } : {}),
        ...(dias ? { dias } : {}),
        // TUT-476 — o período de vencimento e a conclusão da Medição.
        ...(periodoDe ? { periodoDe } : {}),
        ...(periodoAte ? { periodoAte } : {}),
        ...(concluidoDe ? { concluidoDe } : {}),
        ...(concluidoAte ? { concluidoAte } : {}),
        ...(noPrazo ? { noPrazo } : {}),
      });
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (type) params.set("type", type);
    if (assignedUserId) params.set("assignedUserId", assignedUserId);
    if (subjectId) params.set("subjectId", subjectId);
    if (clientOrganizationId)
      params.set("clientOrganizationId", clientOrganizationId);
    if (vence) params.set("vence", vence);
    if (dias) params.set("dias", String(dias));
    if (periodoDe) params.set("periodoDe", periodoDe);
    if (periodoAte) params.set("periodoAte", periodoAte);
    if (concluidoDe) params.set("concluidoDe", concluidoDe);
    if (concluidoAte) params.set("concluidoAte", concluidoAte);
    if (noPrazo) params.set("noPrazo", "true");
    return request<DeadlineListResponse>(
      `/deadlines${comFatia(params, { page, pageSize, sort, dir })}`,
    );
  },
  // Catálogo de tipos de prazo (TUT-124).
  deadlineTypes(includeInactive = false): Promise<DeadlineTypeListResponse> {
    if (useDemoApi) return demoApi.deadlineTypes(includeInactive);
    const query = includeInactive ? "?includeInactive=true" : "";
    return request<DeadlineTypeListResponse>(`/deadlines/types${query}`);
  },
  createDeadlineType(label: string): Promise<DeadlineTypeSummary> {
    if (useDemoApi) return demoApi.createDeadlineType(label);
    return request<DeadlineTypeSummary>("/deadlines/types", {
      method: "POST",
      body: JSON.stringify({ label }),
    });
  },
  setDeadlineTypeActive(
    id: string,
    active: boolean,
  ): Promise<DeadlineTypeSummary> {
    if (useDemoApi) return demoApi.setDeadlineTypeActive(id, active);
    return request<DeadlineTypeSummary>(
      `/deadlines/types/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify({ active }) },
    );
  },
  createDeadline(
    processId: string,
    input: CreateDeadlineInput,
  ): Promise<DeadlineItem> {
    return useDemoApi
      ? demoApi.createDeadline(processId, input)
      : request<DeadlineItem>(
          `/processes/${encodeURIComponent(processId)}/deadlines`,
          {
            method: "POST",
            body: JSON.stringify(input),
          },
        );
  },
  // TUT-495 — os feriados do escritório e a contagem em dias úteis.
  officeHolidays(): Promise<OfficeHolidayListResponse> {
    if (useDemoApi) return demoApi.officeHolidays();
    return request<OfficeHolidayListResponse>("/deadlines/holidays");
  },
  createOfficeHoliday(input: CreateOfficeHolidayInput): Promise<OfficeHoliday> {
    if (useDemoApi) return demoApi.createOfficeHoliday(input);
    return request<OfficeHoliday>("/deadlines/holidays", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  deleteOfficeHoliday(id: string): Promise<{ removed: boolean }> {
    if (useDemoApi) return demoApi.deleteOfficeHoliday(id);
    return request<{ removed: boolean }>(
      `/deadlines/holidays/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },
  businessDaysPreview(
    startDate: string,
    businessDays: number,
  ): Promise<BusinessDaysPreview> {
    if (useDemoApi) return demoApi.businessDaysPreview(startDate, businessDays);
    const params = new URLSearchParams({
      from: startDate,
      days: String(businessDays),
    });
    return request<BusinessDaysPreview>(`/deadlines/business-days?${params}`);
  },
  assignableMembers(): Promise<AssignableMembersResponse> {
    return useDemoApi
      ? demoApi.assignableMembers()
      : request<AssignableMembersResponse>("/deadlines/assignable-members");
  },
  updateDeadline(
    id: string,
    input: UpdateDeadlineInput,
  ): Promise<DeadlineItem> {
    if (useDemoApi) return demoApi.updateDeadline(id, input);
    return request<DeadlineItem>(`/deadlines/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
};
